import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileText, Download, Eye, Search, File, Image as ImageIcon,
  Folder, RefreshCw, List, Grid3X3, SortAsc, SortDesc, Filter
} from 'lucide-react';
import axios from 'axios';
import { cn } from '../lib/utils';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  bl: FileText, cmr: FileText, facture: FileText, devis: FileText,
  recepisse: FileText, photo_chargement: ImageIcon, photo_dechargement: ImageIcon,
  bon_commande: FileText, autre: File
};

const TYPE_COLORS = {
  bl: 'bg-blue-50 text-blue-700 border-blue-200',
  cmr: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  facture: 'bg-green-50 text-green-700 border-green-200',
  devis: 'bg-amber-50 text-amber-700 border-amber-200',
  recepisse: 'bg-purple-50 text-purple-700 border-purple-200',
  photo_chargement: 'bg-pink-50 text-pink-700 border-pink-200',
  photo_dechargement: 'bg-rose-50 text-rose-700 border-rose-200',
  bon_commande: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  autre: 'bg-gray-50 text-gray-700 border-gray-200'
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes > 1e6) return `${(bytes / 1e6).toFixed(1)} Mo`;
  if (bytes > 1e3) return `${(bytes / 1e3).toFixed(0)} Ko`;
  return `${bytes} o`;
};

const ClientDocuments = () => {
  const permissions = useClientPermissions();
  const [documents, setDocuments] = useState([]);
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('date_desc');

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.doc_type = typeFilter;
      const res = await axios.get(`${API}/client/documents`, { params });
      setDocuments(res.data.documents || []);
      setTypes(res.data.types || {});
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const filtered = useMemo(() => {
    let result = documents.filter(d =>
      !searchTerm || (d.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.operationNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.operationRoute || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    result.sort((a, b) => {
      if (sortBy === 'date_desc') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (sortBy === 'date_asc') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'size_desc') return (b.size || 0) - (a.size || 0);
      return 0;
    });
    return result;
  }, [documents, searchTerm, sortBy]);

  // Stats by type
  const typeStats = useMemo(() => {
    const counts = {};
    documents.forEach(d => {
      const t = d.type || 'autre';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [documents]);

  const totalSize = useMemo(() => documents.reduce((sum, d) => sum + (d.size || 0), 0), [documents]);

  const isImageType = (type) => ['photo_chargement', 'photo_dechargement'].includes(type);

  return (
    <div className="space-y-6" data-testid="client-documents">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Mes documents</h1>
          <p className="text-gray-500 mt-1">
            {documents.length} document{documents.length > 1 ? 's' : ''}
            {totalSize > 0 && <span className="ml-2 text-gray-400">({formatSize(totalSize)} total)</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDocuments} disabled={loading} data-testid="refresh-docs-btn">
          <RefreshCw className={cn("w-4 h-4 mr-1", loading && "animate-spin")} />
          Actualiser
        </Button>
      </div>

      {/* Type stats cards */}
      {Object.keys(typeStats).length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="doc-type-stats">
          {Object.entries(typeStats).map(([type, count]) => {
            const Icon = TYPE_ICONS[type] || File;
            const colorClass = TYPE_COLORS[type] || TYPE_COLORS.autre;
            const isActive = typeFilter === type;
            return (
              <button
                key={type}
                onClick={() => setTypeFilter(isActive ? 'all' : type)}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-all',
                  isActive ? 'bg-[#1db584] text-white border-[#1db584]' : colorClass,
                  'hover:opacity-80'
                )}
                data-testid={`type-filter-${type}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {types[type] || type} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Search + Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un document, opération..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1db584] text-sm"
                data-testid="doc-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]" data-testid="doc-sort">
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Plus récents</SelectItem>
                  <SelectItem value="date_asc">Plus anciens</SelectItem>
                  <SelectItem value="name_asc">Nom A-Z</SelectItem>
                  <SelectItem value="size_desc">Taille</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={cn('p-2 transition-colors', viewMode === 'list' ? 'bg-[#1db584] text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn('p-2 transition-colors', viewMode === 'grid' ? 'bg-[#1db584] text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
                  data-testid="view-grid-btn"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <LoadingSpinner className="h-40" />
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={Folder}
              title="Aucun document"
              description={searchTerm ? `Aucun résultat pour "${searchTerm}"` : "Les documents liés à vos opérations apparaîtront ici"}
            />
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="doc-grid-view">
          {filtered.map((doc) => {
            const Icon = TYPE_ICONS[doc.type] || File;
            const colorClass = TYPE_COLORS[doc.type] || TYPE_COLORS.autre;
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow group" data-testid={`doc-${doc.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col items-center text-center">
                    <div className={cn('p-4 rounded-xl mb-3', colorClass.split(' ')[0])}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <p className="font-medium text-gray-900 text-sm truncate w-full">{doc.name || 'Document'}</p>
                    <Badge variant="outline" className={cn('text-[10px] mt-1', colorClass)}>{doc.typeLabel}</Badge>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(doc.createdAt)}</p>
                    {doc.operationNumber && <p className="text-xs text-gray-500 mt-0.5">{doc.operationNumber}</p>}
                    <div className="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      {doc.url && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                            <a href={doc.url} target="_blank" rel="noopener noreferrer"><Eye className="w-3.5 h-3.5" /></a>
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                            <a href={doc.url} download><Download className="w-3.5 h-3.5" /></a>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="space-y-2" data-testid="doc-list-view">
          {filtered.map((doc) => {
            const Icon = TYPE_ICONS[doc.type] || File;
            const colorClass = TYPE_COLORS[doc.type] || TYPE_COLORS.autre;
            return (
              <Card key={doc.id} className="hover:shadow-sm transition-shadow" data-testid={`doc-${doc.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={cn('p-2.5 rounded-lg', colorClass.split(' ')[0])}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{doc.name || 'Document'}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                        <Badge variant="outline" className={cn('text-xs', colorClass)}>{doc.typeLabel}</Badge>
                        {doc.operationNumber && <span>{doc.operationNumber}</span>}
                        {doc.operationRoute && <span className="truncate max-w-[200px]">{doc.operationRoute}</span>}
                        <span>{formatDate(doc.createdAt)}</span>
                        {doc.size > 0 && <span>{formatSize(doc.size)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {doc.url && (
                        <>
                          <Button variant="ghost" size="icon" asChild data-testid={`view-doc-${doc.id}`}>
                            <a href={doc.url} target="_blank" rel="noopener noreferrer"><Eye className="w-4 h-4" /></a>
                          </Button>
                          <Button variant="ghost" size="icon" asChild data-testid={`download-doc-${doc.id}`}>
                            <a href={doc.url} download><Download className="w-4 h-4" /></a>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Footer stats */}
      {filtered.length > 0 && (
        <div className="text-center text-sm text-gray-400 pb-4">
          {filtered.length} document{filtered.length > 1 ? 's' : ''} affichés
          {typeFilter !== 'all' && ` (filtre: ${types[typeFilter] || typeFilter})`}
        </div>
      )}
    </div>
  );
};

export default ClientDocuments;
